import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../api/document';
import {
  DocumentApprovalType,
  DocumentStatus,
} from '../../../../../api/model/document';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderRevertToDraftButton } from '../../RenderLogic';
import { useFeatureFlagValue } from '../../../../../hooks/useFeatureFlagValue';
import { useRevertSequentialToDraft } from './useRevertSequentialToDraft';

export const RevertToDraftButton = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument, refetchDocumentOverviewData, currentEditorMode } =
    useContext(DocumentOverviewContext);
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToastProvider();
  const isNewTemplateUpgradeEnabled = useFeatureFlagValue(
    'new-template-upgrade',
  );
  const { revertSequentialToDraft } = useRevertSequentialToDraft();

  const isSequentialApproval =
    qualioDocument.approval_type === DocumentApprovalType.Sequential;

  const revertToDraft = () =>
    isSequentialApproval
      ? revertSequentialToDraft({ documentId: qualioDocument.id })
      : documentApi.changeStatus(qualioDocument.id, {
          status: DocumentStatus.Draft,
        });

  const handleRevertToDraft = () => {
    setDisabled(true, setIsLoading);
    revertToDraft()
      .then(() => {
        setDisabled(false, setIsLoading);
        refetchDocumentOverviewData();
        showToast({
          'data-cy': 'revert-to-draft-button-notify-success-toast',
          description: t('success.successfullyRevertedToDraft'),
          status: 'success',
          title: t('notifications.success'),
        });
      })
      .catch((e) => {
        setDisabled(false, setIsLoading);
        const failureMessage =
          e.response?.data?.message ||
          t('documentActions.failedToRevertToDraft');
        showToast({
          'data-cy': 'revert-to-draft-error-toast',
          id: 'revert-to-draft-error-toast',
          status: 'error',
          title: t('notifications.error'),
          description: failureMessage,
        });
      });
  };

  if (
    !shouldRenderRevertToDraftButton(
      qualioDocument,
      currentUser,
      currentEditorMode,
      isNewTemplateUpgradeEnabled,
    )
  ) {
    return null;
  }

  return (
    <QButton
      isLoading={isLoading}
      isDisabled={isDisabled}
      variant="outline"
      onClick={handleRevertToDraft}
      data-cy="doc-overview-revert-to-draft-button"
    >
      {t('buttons.revertToDraft')}
    </QButton>
  );
};
